import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Paper, Container, Typography, Modal, Radio, RadioGroup, FormControlLabel, FormControl, Box, LinearProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import useInviteCodeServices from '../../hooks/useInviteCodeServices';
import StyledButton from '../general/StyledButton';

const useStyles = makeStyles((theme) => ({
  buttonSection: {
    alignItems: 'center',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    padding: '2rem 1rem 0 1rem',
    width: '96%',
  },
  closeButton: {
    color: '#000',
    fontSize: '2rem',
    position: 'absolute',
    top: '1rem',
    right: '1rem',
  },
  errorSection: {
    display: 'flex',
    flexDirection: 'column',
    color: '#c62828',
  },
  header: {
    fontSize: '1.6rem',
    fontWeight: 'bold',
    paddingBottom: '0.5rem',
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'row',
  },
  loadingSection: {
    paddingTop: '1rem',
    '& .MuiLinearProgress-colorPrimary': {
      backgroundColor: '#e0e0e0',
    },
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: '#009688 ',
    },
  },
  paper: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    height: '35%',
    maxHeigth: '100%',
    padding: '2rem 0 2rem 0',
    position: 'relative',
    overflow: 'auto',
    width: '60%',
  },
  radioGroup: {
    '& .MuiFormControlLabel-label': {
      color: '#000',
      fontSize: '1rem',
    },
    '& .Mui-checked': {
      color: '#000',
    },
  },
  displaySection: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: '10rem',
    marginTop: '1rem',
  },
  subtitle: {
    fontWeight: 'bold',
  },
  modal: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '3rem',
  },
}));

const BatchInviteModal = ({ openStatus, handleClose }) => {
  const classes = useStyles();

  const DEFAULT_PLACEHOLDER = 'ALL';

  const DEFAULT_TITLE = 'Batch Invite';
  const DEFAULT_SUBTITLE = 'Select how many invite codes you would like to send:';

  const CONFIRM_TITLE = 'Confirm Batch Invite';
  const CONFIRM_SUBTITLE = 'Please check if the Batch Invite Code details are correct. If yes, please proceed.';

  const LOADING_TITLE = 'Sending Invite Codes';

  const FINISH_TITLE = 'Batch Invite Sent!';

  // radio group choices
  const OPTION1_LABEL = 'All Uninvited';
  const OPTION1 = 'SEND_TO_ALL';

  const { loading, emailErrorMessages, numberOfInviteCodeSent, sendInviteCodeToAllProfiles } = useInviteCodeServices();

  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE);

  const [country, setCountry] = useState(DEFAULT_PLACEHOLDER);
  const [state, setState] = useState(DEFAULT_PLACEHOLDER);

  const [selectedRadioOption, setSelectedRadioOption] = useState(OPTION1);

  // step 1 - select a invite code function
  const [step1, setStep1] = useState(true);

  // step 2 - confirm selection
  const [step2, setStep2] = useState(false);

  // step 3 - show loading progress
  const [step3, setStep3] = useState(false);

  // step 4 - finish executing selected function
  const [step4, setStep4] = useState(false);

  // === Event Handler Functions ===

  const handleConfirmBatchInviteButton = (e) => {
    setStep1(false);
    setStep2(true);
    setTitle(CONFIRM_TITLE);
    setSubtitle(CONFIRM_SUBTITLE);
  };

  const handleSendBatchInviteButton = (e) => {
    setStep2(false);
    setStep3(true);
    setTitle(LOADING_TITLE);
    setSubtitle('');

    // TODO refactor this later to a switch statement when more radio options are added
    // execute selected invite code function(s)
    if (selectedRadioOption === OPTION1) {
      sendInviteCodeToAllProfiles();
    }
  };

  const finishSendingInviteCode = () => {
    setStep3(false);
    setStep4(true);
    setTitle(FINISH_TITLE);
  };

  const handleReset = () => {
    // reset to defaults
    setStep1(true);
    setStep2(false);
    setStep3(false);
    setStep4(false);

    setTitle(DEFAULT_TITLE);
    setSubtitle(DEFAULT_SUBTITLE);

    setCountry(DEFAULT_PLACEHOLDER);
    setState(DEFAULT_PLACEHOLDER);

    handleClose();
  };

  // === Component Render Functions ===

  const renderInviteCountryStateDisplay = (codeSent = false) => {
    return (
      <Container className={classes.displaySection}>
        {codeSent && (
          <Box>
            <h6>Number of Invite Code Sent</h6>
            <b>{numberOfInviteCodeSent}</b>
          </Box>
        )}
        <Box>
          <h6>Country</h6>
          <b>{country}</b>
        </Box>
        <Box>
          <h6>State/Province</h6>
          <b>{state}</b>
        </Box>
      </Container>
    );
  };

  const renderStep1Content = () => {
    return (
      <>
        <Container>
          <FormControl>
            <RadioGroup
              className={classes.radioGroup}
              aria-label="select-invite-code-functions-label"
              name="select-invite-code-functions"
              value={selectedRadioOption}
              onChange={(e) => {
                setSelectedRadioOption(e.target.value);
              }}>
              <FormControlLabel value={OPTION1} control={<Radio />} label={OPTION1_LABEL} />
            </RadioGroup>
          </FormControl>
        </Container>
        <Container className={classes.buttonSection}>
          <StyledButton text={'CONFIRM BATCH INVITE'} onClickHandler={handleConfirmBatchInviteButton} />
        </Container>
      </>
    );
  };

  const renderStep2Content = () => {
    return (
      <>
        {renderInviteCountryStateDisplay()}
        <Container className={classes.buttonSection}>
          <StyledButton text={'SEND BATCH INVITE'} onClickHandler={handleSendBatchInviteButton} />
        </Container>
      </>
    );
  };

  const renderStep3Content = () => {
    return (
      <Container className={classes.loadingSection}>
        {loading && <LinearProgress />}
        {!loading && finishSendingInviteCode()}
      </Container>
    );
  };

  const renderErrorMessages = () => {
    return (
      <Container className={classes.errorSection}>
        {emailErrorMessages.map((error) => (
          <p>{error}</p>
        ))}
      </Container>
    );
  };

  const renderStep4Content = () => {
    return (
      <>
        {renderInviteCountryStateDisplay(true)}
        {emailErrorMessages.length > 0 && renderErrorMessages()}
        <Container className={classes.buttonSection}>
          <StyledButton text={'GO BACK TO DASHBOARD'} onClickHandler={handleReset} />
        </Container>
      </>
    );
  };

  return (
    <Modal className={classes.modal} open={openStatus} onClose={handleReset} aria-labelledby="Batch-Invite-Modal" disableBackdropClick>
      <Paper className={classes.paper}>
        <IconButton className={classes.closeButton} onClick={handleReset}>
          <CloseIcon />
        </IconButton>
        <Container className={classes.headerSection}>
          <Typography className={classes.header}>{title}</Typography>
        </Container>
        <Container>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </Container>
        {step1 && renderStep1Content()}
        {step2 && renderStep2Content()}
        {step3 && renderStep3Content()}
        {step4 && renderStep4Content()}
      </Paper>
    </Modal>
  );
};

export default BatchInviteModal;
